/**
 * CallRail Adapter — Story 22.1 (opens Epic 22 Call Tracking slice)
 *
 * **Auth model:** Per-user API token sent as the value of a custom
 * `Authorization: Token token="<API_KEY>"` HTTP header. The customer
 * pastes their CallRail API token (generated in the CallRail API keys
 * dashboard, `app.callrail.com/account/api/keys`) into the Connect
 * dialog; we encrypt it via the same `encryptToken` helper that OAuth
 * tokens use and store it in `oauth_tokens` under `provider="callrail"`.
 * See AC #3 in the Story 22.1 spec for why we deliberately reuse the
 * OAuth table for an API token.
 *
 * **DELIBERATE divergences from Moz (Story 20.7) — internalize before
 * reaching for the Moz files as a copy template:**
 *
 *  1. **Auth shape — `Authorization: Token token="<KEY>"` HTTP header.**
 *     CallRail uses a NON-STANDARD Authorization value: literally the word
 *     "Token" followed by a space, then `token="..."` with double quotes
 *     around the key. NOT `Bearer <KEY>` (Ahrefs), NOT `?key=` query
 *     param (Semrush), NOT `x-moz-token: <value>` (Moz). Verified
 *     `apidocs.callrail.com/` and
 *     `support.callrail.com/hc/en-us/articles/5711821845389-CallRail-s-API`
 *     2026-05-14.
 *
 *  2. **Wire format — REST + JSON (NOT JSON-RPC like Moz, NOT CSV like
 *     Semrush, NOT path-based REST like Ahrefs).** Every CallRail v3 call
 *     is a standard REST GET at account-scoped paths
 *     (`/v3/a/{account_id}/calls.json` etc.); responses are plain JSON.
 *     The simplest wire format of any API-key adapter we've shipped.
 *
 *  3. **Required account discovery via 2-step wizard.** CallRail keys are
 *     user-scoped within an organization and a single API key may have
 *     access to N accounts (multi-account orgs are common for resellers,
 *     e.g. a PMP managing 30 client accounts under one master key). The
 *     adapter calls `GET /v3/a.json` at connect time to discover
 *     accessible accounts and requires the user to pick an account in
 *     the connect dialog's Step 2. The selected `account_id` is
 *     persisted as `integrations.account_id`. FIRST API-key dialog with
 *     2-step state in our library.
 *
 *  4. **25-month hard data-retention pre-flight at `fetchMetrics`.** Per
 *     `support.callrail.com/hc/en-us/articles/5711337552269-CallRail-s-data-retention-policy`
 *     verified 2026-05-14, Communication Records are auto-deleted after
 *     25 months. The adapter pre-flight rejects synchronously via
 *     `validateCallRailDateRangeAgainstRetention` BEFORE any API call.
 *     FIRST adapter in our library with a hard-retention pre-flight.
 *
 * **Customer PII flows through `fetchMetrics`.** Unlike SEO tools where
 * the API surface is business-aggregate, CallRail's `/calls.json` and
 * `/form_submissions.json` return per-call records with caller-identifying
 * data (`customer_phone_number`, `customer_name`, `customer_email`, etc.)
 * in the DEFAULT response. The `sanitizeCallRailPii` helper at
 * `callrail/pii.ts` MUST run on EVERY record before the record enters
 * any log emission, Sentry breadcrumb, or queue-error payload (Round 3
 * Red Team Attack #1).
 *
 * **Recording URLs are stripped at the transformer**, not the log
 * boundary. CallRail's `recording` field is a signed URL pointing at
 * `/v3/a/{account_id}/calls/{call_id}/recording` — if a buggy widget
 * echoed this cross-tenant, it would be a data leak. Story 22.1 deliberately
 * does NOT persist recording URLs; widgets that need them request via a
 * dedicated tenant-scoped route (deferred to Story 22.5). Round 3 Red
 * Team Attack #2.
 *
 * **Web research divergences (verified `apidocs.callrail.com/` 2026-05-14)
 * vs the story AC text — see story Completion Notes for full citations:**
 *
 *  - Company filtering uses `?company_id=` query parameter, NOT
 *    `/c/{company_id}/` URL path. Implemented in `api.ts` via
 *    `appendCompanyIdQuery` per the user's Option-1 decision (real-API
 *    shape with documented divergence).
 *  - `granularity` accepts `hour | day | week | month | year` (story
 *    listed only 3 values).
 *  - Timeseries capped at **200 data points per response** — warning
 *    surfaced when exceeded.
 *  - Default `/calls.json` fields include PII + recording URL —
 *    sanitizers run on every record.
 *  - No documented `Retry-After` header on 429 — 60-sec cooldown
 *    fallback applied in `safe-fetch.ts`.
 *  - No structured error response body schema — `parseCallRailApiError`
 *    defensively probes multiple JSON field names.
 *
 * **NO row-budget circuit-breaker** (DIVERGENT from Moz 20.7's
 * 2K-rows/day, Semrush 20.4's 200K-units/day, Ahrefs 20.1's
 * 5M-units/day). CallRail does NOT bill per-row; the only quotas are
 * request-count (1K/hr + 10K/day per
 * `rollout.com/integration-guides/call-rail/api-essentials` verified
 * 2026-05-14). Existing queue rate-limit throttling is sufficient.
 *
 * **NO snapshot persistence** (DIVERGENT from Moz 20.7's
 * `moz_authority_snapshots` D1 table). CallRail's
 * `/calls/timeseries.json` provides server-side time-series aggregation
 * natively — no ProvenLeap-side snapshot table is required to chart
 * "calls over time".
 *
 * **NO entry in `API_KEY_PLATFORMS`** (per AC #11 — that set is reserved
 * for env-binding API-key platforms like `"google-pagespeed"`). CallRail
 * is per-user-secret like Ahrefs + Semrush + Moz, NOT env-key.
 *
 * **Future CallRail expansion methods (deferred):** SMS endpoints
 * (`/text-messages`), outbound-call endpoints, agent dimension, tracker
 * dimension, Conversation Intelligence advanced fields (`call_highlights`,
 * `keywords_spotted`, `lead_score_explanation`, `voicemail_transcription`)
 * — catalogued at `_bmad/bmm/data/integration-metrics/callrail.md` as
 * `gap` rows. Story 22.4 / 22.5 expand them. Adding new endpoints is
 * purely additive (new helpers in `api.ts` + new transformers +
 * `MetricDefinition` rows).
 */

import {
  adapterErr,
  adapterOk,
  type AccountInfo,
  type AdapterContext,
  type AdapterMetrics,
  type AdapterResult,
  type DimensionDefinition,
  type EndpointDiscoveryInfo,
  type IntegrationAdapter,
  type MetricDefinition,
} from "../adapter";
import type { Platform } from "@provenleap/db";
import { API_VERSIONS } from "../config";

import {
  discoverCallRailAccounts,
  fetchAllCallRailCalls,
  fetchAllCallRailFormSubmissions,
  fetchAllCallRailTextMessages,
  fetchCallRailCallsSummary,
  fetchCallRailCallsTimeseries,
  validateCallRailConnection,
  CALLRAIL_API_BASE,
  type CallRailAccount,
} from "./api";
import { CallRailApiCallError } from "./errors";
import {
  CallRailRetentionViolationError,
  validateCallRailDateRangeAgainstRetention,
} from "./retention";
import { sanitizeCallRailPii } from "./pii";

// ============================================================================
// Constants
// ============================================================================

const PLATFORM = "callrail" as const;

export const CALLRAIL_PLATFORM_VALUES = ["callrail"] as const;
export type CallRailPlatform = (typeof CALLRAIL_PLATFORM_VALUES)[number];

export const CALLRAIL_API_VERSION = API_VERSIONS.callrail;

/** Public re-export of the base URL constant from `api.ts`. */
export { CALLRAIL_API_BASE };

/**
 * Hours of staleness stamped on `metadata.dataFreshnessLagHours`. CallRail
 * processes calls near-real-time but the post-call webhook ceiling is 20
 * minutes per `support.callrail.com/hc/en-us/articles/5711246459149-Webhooks`
 * verified 2026-05-14. Round-up to 1 hour for widget caption consistency
 * with other realtime-leaning adapters (Shopify also emits 1).
 */
export const CALLRAIL_FRESHNESS_LAG_HOURS = 1;

// ============================================================================
// CALLRAIL_METRICS — full catalogue (44 of 47 metrics)
// ============================================================================

/**
 * **Story 22.1 ships 44 of 47 catalogued CallRail metrics** — the 3
 * remaining rows are the SMS Conversation family (Total SMS Conversations,
 * First Text, Last Message Time) which need a dedicated `/text-messages`
 * endpoint helper and are scoped to a future Story 22.x SMS expansion.
 *
 * Build-to-sell rationale: Stories 22.3/22.4 are for the CallTrackingMetrics
 * integration (a different platform). There is no separate "CallRail
 * expansion" story scheduled — so unless 22.1 ships everything CallRail
 * has, the remaining metrics stay `gap` forever. The full catalogue ships
 * here.
 *
 * Groups:
 *  - Call Volume & Counts (9)
 *  - Duration & Time (6 — 1 CI-gated)
 *  - Derived Quality Rates (6)
 *  - Value & Revenue (3)
 *  - Conversation Intelligence — SUBSCRIPTION-GATED (9)
 *  - Call Attribution & Identity (7)
 *  - Form Submissions (4)
 *
 * Conversation Intelligence metrics carry a `category = "Conversation
 * Intelligence"` marker so the widget palette can dim them when the
 * `discoverEndpoints` snapshot reports `subscription_required` on the CI
 * endpoint.
 */
export const CALLRAIL_METRICS: ReadonlyArray<MetricDefinition> = [
  // Call Volume & Counts (9)
  {
    key: "totalCalls",
    label: "Total Calls",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call",
    apiField: "total_calls",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"],
    description: "The headline CallRail metric — total inbound calls received in the date range.",
  },
  {
    key: "answeredCalls",
    label: "Answered Calls",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call",
    apiField: "answered_calls",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  {
    key: "missedCalls",
    label: "Missed Calls",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call",
    apiField: "missed_calls",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  {
    key: "firstTimeCallers",
    label: "First-Time Callers",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call",
    apiField: "first_time_callers",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  {
    key: "leads",
    label: "Leads",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call",
    apiField: "leads",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
  },
  {
    key: "voicemails",
    label: "Voicemails",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call",
    compatibleWidgets: ["kpi", "bar-chart"],
    description: "Derived: count of calls where answer_status='voicemail'.",
  },
  {
    key: "inboundCalls",
    label: "Inbound Calls",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call",
    compatibleWidgets: ["kpi", "pie-chart"],
    description: "Derived: count of calls where direction='inbound'.",
  },
  {
    key: "outboundCalls",
    label: "Outbound Calls",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call",
    compatibleWidgets: ["kpi", "pie-chart"],
    description: "Derived: count of calls where direction='outbound'.",
  },
  {
    key: "totalValue",
    label: "Total Call Value",
    type: "currency",
    dataType: "Currency",
    aggregationKind: "additive",
    category: "Call",
    apiField: "total_value",
    compatibleWidgets: ["kpi", "bar-chart"],
  },
  {
    key: "totalRevenue",
    label: "Total Revenue (aggregated)",
    type: "currency",
    dataType: "Currency",
    aggregationKind: "additive",
    category: "Call",
    compatibleWidgets: ["kpi", "bar-chart"],
    description: "Derived: sum of per-call `value` across the date range; alias for totalValue when no grouping is applied.",
  },
  {
    key: "averageDuration",
    label: "Average Call Duration",
    type: "duration",
    dataType: "Duration",
    aggregationKind: "ratio", ratioComponents: { denominator: "totalCalls" },
    category: "Call",
    apiField: "average_duration",
    compatibleWidgets: ["kpi", "line-chart", "gauge"],
  },
  {
    key: "formattedAverageDuration",
    label: "Avg Call Duration (Formatted)",
    type: "duration",
    dataType: "Duration",
    aggregationKind: "ratio", ratioComponents: { denominator: "totalCalls" },
    category: "Call",
    apiField: "formatted_average_duration",
    compatibleWidgets: ["kpi", "data-table"],
  },
  // Calls List per-call (10)
  {
    key: "callDuration",
    label: "Call Duration",
    type: "duration",
    dataType: "Duration",
    aggregationKind: "additive",
    category: "Call",
    apiField: "duration",
    compatibleWidgets: ["data-table", "kpi"],
  },
  {
    key: "formattedCallDuration",
    label: "Call Duration (Formatted)",
    type: "duration",
    dataType: "Duration",
    aggregationKind: "additive",
    category: "Call",
    apiField: "formatted_duration",
    compatibleWidgets: ["data-table"],
  },
  {
    key: "recordingDuration",
    label: "Recording Duration",
    type: "duration",
    dataType: "Duration",
    aggregationKind: "additive",
    category: "Call",
    apiField: "recording_duration",
    compatibleWidgets: ["data-table"],
  },
  {
    key: "callValue",
    label: "Call Value",
    type: "currency",
    dataType: "Currency",
    aggregationKind: "additive",
    category: "Call",
    apiField: "value",
    compatibleWidgets: ["kpi", "data-table"],
  },
  {
    key: "formattedCallValue",
    label: "Call Value (Formatted)",
    type: "currency",
    dataType: "Currency",
    aggregationKind: "additive",
    category: "Call",
    apiField: "formatted_value",
    compatibleWidgets: ["data-table"],
  },
  {
    key: "priorCalls",
    label: "Prior Calls",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call",
    apiField: "prior_calls",
    compatibleWidgets: ["data-table"],
  },
  {
    key: "personId",
    label: "Person ID",
    type: "number",
    // Pass 1 review fix: CallRail's `person_id` is a string identifier
    // (alphanumeric), not an integer. Use the `Enum` dataType to mark
    // it as a categorical-ID for widget rendering — Integer would
    // mislead widgets into numeric aggregation.
    dataType: "Enum",
    category: "Call",
    apiField: "person_id",
    compatibleWidgets: ["data-table"],
    description: "Caller-identity grouping ID (string) — added by CallRail 2024-05-16 for repeat-caller analysis.",
  },
  {
    key: "firstCallFlag",
    label: "First Call",
    type: "number",
    // Pass 1 review fix: CallRail's `first_call` is a boolean field.
    // Use `Enum` to signal a categorical (true/false) value rather
    // than `Integer` (which would imply numeric aggregation).
    dataType: "Enum",
    category: "Call",
    apiField: "first_call",
    compatibleWidgets: ["data-table"],
  },
  {
    key: "leadStatus",
    label: "Lead Status",
    type: "number",
    dataType: "Enum",
    category: "Quality",
    apiField: "lead_status",
    compatibleWidgets: ["kpi", "pie-chart", "data-table"],
    description: "Values: good_lead / not_a_lead / not_scored / previously_marked_good_lead.",
  },
  {
    key: "callType",
    label: "Call Type",
    type: "number",
    dataType: "Enum",
    category: "Call",
    apiField: "call_type",
    compatibleWidgets: ["data-table"],
  },
  // Calls Timeseries (1)
  {
    key: "dailyCallsByDate",
    label: "Daily Calls by Date",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Call",
    apiField: "total_calls",
    compatibleWidgets: ["line-chart"],
    description: "Time-series shape driving the 'Calls Over Time' widget. Backed by /calls/timeseries.json.",
  },
  // Form Submissions (2)
  {
    key: "totalFormSubmissions",
    label: "Total Form Submissions",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Conversion",
    apiField: "form_submissions_count",
    compatibleWidgets: ["kpi", "line-chart", "bar-chart"],
    description: "Total form-submission count over the date range, derived from /form_submissions.json.",
  },
  {
    key: "firstFormSubmissions",
    label: "First-Form Submissions",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Conversion",
    apiField: "first_form_submissions_count",
    compatibleWidgets: ["kpi", "bar-chart"],
    description: "Count of submissions where first_form=true (initial customer touchpoint).",
  },
  // Derived Quality Rates (5, client-side derived from Summary counts)
  {
    key: "answerRate",
    label: "Answer Rate",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "answeredCalls", denominator: "totalCalls" },
    category: "Performance",
    compatibleWidgets: ["kpi", "gauge"],
    description: "Derived: answered_calls / total_calls. Computed at the transformer, no API field.",
  },
  {
    key: "missRate",
    label: "Miss Rate",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "missedCalls", denominator: "totalCalls" },
    category: "Performance",
    compatibleWidgets: ["kpi", "gauge"],
    description: "Derived: missed_calls / total_calls.",
  },
  {
    key: "firstTimeCallerRate",
    label: "First-Time Caller Rate",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "firstTimeCallers", denominator: "totalCalls" },
    category: "Performance",
    compatibleWidgets: ["kpi", "gauge"],
    description: "Derived: first_time_callers / total_calls.",
  },
  {
    key: "leadConversionRate",
    label: "Lead Conversion Rate",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "leads", denominator: "totalCalls" },
    category: "Performance",
    compatibleWidgets: ["kpi", "gauge"],
    description: "Derived: leads / total_calls.",
  },
  {
    key: "repeatCallerCount",
    label: "Repeat Caller Count",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Performance",
    compatibleWidgets: ["kpi", "bar-chart"],
    description: "Derived: total_calls - first_time_callers.",
  },
  {
    key: "voicemailRate",
    label: "Voicemail Rate",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { numerator: "voicemails", denominator: "totalCalls" },
    category: "Performance",
    compatibleWidgets: ["kpi", "gauge"],
    description: "Derived: voicemail count / total_calls.",
  },
  {
    key: "abandonedCallRate",
    label: "Abandoned Call Rate",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "totalCalls" },
    category: "Performance",
    compatibleWidgets: ["kpi", "gauge"],
    description: "Derived: count of calls with duration < 15s / total_calls.",
  },
  // Conversation Intelligence — SUBSCRIPTION-GATED (4)
  {
    key: "sentiment",
    label: "Sentiment",
    type: "number",
    dataType: "Enum",
    category: "Conversation Intelligence",
    apiField: "sentiment",
    compatibleWidgets: ["kpi", "pie-chart", "data-table"],
    description: "Premium CI: positive / neutral / negative. Requires Premium Conversation Intelligence subscription.",
  },
  {
    key: "transcription",
    label: "Call Transcription",
    type: "number",
    dataType: "Enum",
    category: "Conversation Intelligence",
    apiField: "transcription",
    compatibleWidgets: ["data-table"],
    description: "Premium CI: full call transcript. PII-redacted at the log boundary.",
  },
  {
    key: "leadScore",
    label: "Lead Score",
    type: "number",
    dataType: "Enum",
    category: "Conversation Intelligence",
    apiField: "lead_score",
    compatibleWidgets: ["kpi", "gauge", "data-table"],
    description: "Premium CI: very_poor / poor / fair / good / very_good.",
  },
  {
    key: "callSummary",
    label: "Call Summary",
    type: "number",
    dataType: "Enum",
    category: "Conversation Intelligence",
    apiField: "call_summary",
    compatibleWidgets: ["data-table"],
    description: "Premium CI: AI-generated call summary. PII-redacted at the log boundary.",
  },
  {
    key: "conversationalTranscript",
    label: "Conversational Transcript",
    type: "number",
    dataType: "Array",
    category: "Conversation Intelligence",
    apiField: "conversational_transcript",
    compatibleWidgets: ["data-table"],
    description: "Premium CI: structured transcript (speaker-attributed). PII-redacted at the log boundary.",
  },
  {
    key: "callHighlights",
    label: "Call Highlights",
    type: "number",
    dataType: "Array",
    category: "Conversation Intelligence",
    apiField: "call_highlights",
    compatibleWidgets: ["data-table", "top-posts"],
    description: "Premium CI: AI-extracted call highlights.",
  },
  {
    key: "keywordsSpotted",
    label: "Keywords Spotted",
    type: "number",
    dataType: "Array",
    category: "Conversation Intelligence",
    apiField: "keywords_spotted",
    compatibleWidgets: ["data-table", "bar-chart"],
    description: "Premium CI: keyword-trigger detection per call.",
  },
  {
    key: "leadScoreExplanation",
    label: "Lead Score Explanation",
    type: "number",
    dataType: "Enum",
    category: "Conversation Intelligence",
    apiField: "lead_score_explanation",
    compatibleWidgets: ["data-table"],
    description: "Premium CI: rationale string for the AI-derived lead score.",
  },
  {
    key: "voicemailTranscription",
    label: "Voicemail Transcription",
    type: "number",
    dataType: "Enum",
    category: "Conversation Intelligence",
    compatibleWidgets: ["data-table"],
    description: "Premium CI: derived from `transcription` when voicemail=true. PII-redacted at the log boundary.",
  },
  {
    key: "speakerPercent",
    label: "Speaker Percent (Agent / Customer Split)",
    type: "percentage",
    dataType: "Percentage",
    aggregationKind: "ratio", ratioComponents: { denominator: "callDuration" },
    category: "Conversation Intelligence",
    apiField: "speaker_percent",
    compatibleWidgets: ["data-table", "bar-chart"],
    description: "Premium CI: per-speaker air-time percentage on each call.",
  },
  // Call Attribution & Identity (additional)
  {
    key: "goodLeadCallId",
    label: "Good-Lead Call ID",
    type: "number",
    dataType: "Integer",
    // Story 31-31 Code Review: identifier fact — summing/averaging call IDs
    // is meaningless; no aggregationKind; in NON_NUMERIC_FACT_EXEMPTIONS.
    category: "Quality",
    apiField: "good_lead_call_id",
    compatibleWidgets: ["data-table"],
  },
  {
    key: "goodLeadCallTime",
    label: "Good-Lead Call Time",
    type: "number",
    dataType: "Decimal",
    aggregationKind: "ratio",
    category: "Quality",
    apiField: "good_lead_call_time",
    compatibleWidgets: ["data-table"],
  },
  {
    key: "tagsApplied",
    label: "Tags Applied",
    type: "number",
    dataType: "Array",
    category: "Classification",
    apiField: "tags",
    compatibleWidgets: ["data-table", "bar-chart", "top-posts"],
  },
  // Form Submissions (additional)
  {
    key: "formSubmissionTimestamp",
    label: "Form Submission Timestamp",
    type: "number",
    dataType: "Decimal",
    aggregationKind: "ratio",
    category: "Conversion",
    apiField: "submitted_at",
    compatibleWidgets: ["data-table"],
  },
  {
    key: "formData",
    label: "Form Data (per-field values)",
    type: "number",
    dataType: "Array",
    category: "Conversion",
    apiField: "form_data",
    compatibleWidgets: ["data-table"],
  },
  // Text Messaging (3) — fetched via /text-messages.json endpoint
  {
    key: "totalSmsConversations",
    label: "Total SMS Conversations",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Conversion",
    compatibleWidgets: ["kpi", "line-chart"],
    description: "Count of SMS conversation records returned from /text-messages.json.",
  },
  {
    key: "firstTextFlag",
    label: "First Text (new thread)",
    type: "number",
    dataType: "Integer",
    aggregationKind: "additive",
    category: "Conversion",
    apiField: "first_text",
    compatibleWidgets: ["data-table"],
  },
  {
    key: "lastMessageTime",
    label: "Last Message Time",
    type: "number",
    dataType: "Decimal",
    aggregationKind: "ratio",
    category: "Conversion",
    apiField: "last_message_at",
    compatibleWidgets: ["data-table"],
  },
  // ── Story 22.2 — Synthetic Aggregate Metrics (2) ──────────────────────
  // These 2 metrics are SYNTHETIC AGGREGATES — they do NOT correspond to
  // wire-format CallRail API fields. They are computed at preset-expansion
  // time from `_reportType: "calls-timeseries"` records (90-day daily series).
  //
  // `apiField` is stamped `_synthetic_timeseries_query` so the metric-
  // allowlist build-time test exempts them from the per-endpoint wire-field
  // allowlist check via `CALLRAIL_SYNTHETIC_METRIC_KEYS`. The widget config
  // UI hides these from per-endpoint selectors but exposes them via preset
  // expansions only. Mirrors Moz Story 20.8 `daTrendDirection` pattern.
  {
    key: "callsTrendDirection",
    label: "Calls Trend (90-day)",
    type: "number",
    category: "synthetic-trend",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "_synthetic_timeseries_query",
    synthetic: true,
    description:
      "Synthetic — signed integer = sum(week_N totalCalls) - sum(week_1 totalCalls) over the 90-day calls-timeseries window. Surfaces as '▲ +12 calls' badge on the Cross-Endpoint Scorecard's 5th-candidate slot (deferred in Story 22.2 in favor of leadConversionRate; catalogue row added for future preset use per AC #12 + AC #17).",
  },
  {
    key: "bestCallDayOfWeek",
    label: "Best Call Day of Week",
    type: "number",
    category: "synthetic-trend",
    dataType: "Enum",
    compatibleWidgets: ["kpi"],
    apiField: "_synthetic_timeseries_query",
    synthetic: true,
    description:
      "Synthetic — highest-volume weekday from the 90-day calls-timeseries window (`Monday` ... `Sunday`). Reserved for a future Story 22.x weekday-heatmap widget; catalogue row added now for compositional readiness per AC #12.",
  },
];

/**
 * Story 22.2 — Set of synthetic-aggregate metric keys whose `apiField` is the
 * `_synthetic_timeseries_query` sentinel. The metric-allowlist build-time
 * test exempts these from the per-endpoint wire-field allowlist check.
 * Mirrors the Moz 20.8 `MOZ_SYNTHETIC_METRIC_KEYS` pattern.
 *
 * **DO NOT add a metric here to "fix" a test failure.** This Set is
 * reserved for metrics that are genuinely computed at preset-expand time
 * from time-series aggregate reducers, not from wire-format API fields.
 */
export const CALLRAIL_SYNTHETIC_METRIC_KEYS: ReadonlySet<string> = new Set<string>([
  "callsTrendDirection",
  "bestCallDayOfWeek",
]);

// ============================================================================
// CALLRAIL_DIMENSIONS — full catalogue (36 of 39 dimensions)
// ============================================================================

/**
 * **Story 22.1 ships 36 of 39 catalogued CallRail dimensions** — the 3
 * remaining (lat/long implicit-on-/calls, customer phone number as a
 * grouping dim, and the form_submissions text-conversations join) need
 * deeper widget-rendering work and are scoped to follow-up CallRail
 * widget stories.
 *
 * Build-to-sell rationale: same as CALLRAIL_METRICS — no separate CallRail
 * expansion story is on the backlog (Stories 22.3/22.4 are for
 * CallTrackingMetrics), so the full catalogue ships here.
 */
export const CALLRAIL_DIMENSIONS: ReadonlyArray<DimensionDefinition> = [
  // Time (1)
  { key: "date", label: "Date", type: "Time" },
  // Marketing Source & Attribution (12)
  { key: "source", label: "Source", type: "Traffic" },
  { key: "sourceName", label: "Source Name", type: "Traffic" },
  { key: "campaign", label: "Campaign", type: "Traffic" },
  { key: "keywords", label: "Keywords", type: "Search" },
  { key: "landingPageUrl", label: "Landing Page URL", type: "Traffic" },
  { key: "referrerDomain", label: "Referrer Domain", type: "Traffic" },
  { key: "utmSource", label: "UTM Source", type: "Traffic" },
  { key: "utmMedium", label: "UTM Medium", type: "Traffic" },
  { key: "utmContent", label: "UTM Content", type: "Traffic" },
  { key: "utmTerm", label: "UTM Term", type: "Traffic" },
  { key: "gaClientId", label: "Google Analytics Client ID", type: "Traffic" },
  // Click IDs (3)
  { key: "gclid", label: "Google Click ID (gclid)", type: "Traffic" },
  { key: "fbclid", label: "Facebook Click ID (fbclid)", type: "Traffic" },
  { key: "msclkid", label: "Microsoft Click ID (msclkid)", type: "Traffic" },
  // Organization (1)
  { key: "company", label: "Company", type: "Structure" },
  // Customer Geo (4) — PII-flagged
  { key: "customerCity", label: "Customer City", type: "Geo" },
  { key: "customerState", label: "Customer State", type: "Geo" },
  { key: "customerCountry", label: "Customer Country", type: "Geo" },
  { key: "customerZipCode", label: "Customer Zip Code (geo-routing)", type: "Geo" },
  // Customer Identity (1) — PII-flagged
  { key: "customerName", label: "Customer Name (PII)", type: "Audience" },
  // Device & Channel (4)
  { key: "deviceType", label: "Device Type", type: "Tech", exampleValues: ["desktop", "mobile", "tablet"] },
  { key: "direction", label: "Call Direction", type: "Channel", exampleValues: ["inbound", "outbound"] },
  { key: "callType", label: "Call Type", type: "Classification" },
  { key: "answerStatus", label: "Answer Status", type: "Classification", exampleValues: ["answered", "missed", "voicemail"] },
  // Quality & Intelligence (3)
  { key: "sentimentDim", label: "Sentiment (CI)", type: "Classification", exampleValues: ["positive", "neutral", "negative"] },
  { key: "leadStatusDim", label: "Lead Status (Filter)", type: "Classification", exampleValues: ["good_lead", "not_a_lead", "not_scored", "previously_marked_good_lead"] },
  { key: "tagsDim", label: "Tags / Tag Level", type: "Classification" },
  // Agent (1)
  { key: "agent", label: "Agent", type: "Structure" },
  // Tracker (2)
  { key: "trackingPhoneNumber", label: "Tracking Phone Number", type: "Structure" },
  { key: "trackerId", label: "Tracker ID", type: "Structure" },
  // Session & Path (4)
  { key: "sessionUuid", label: "Session UUID", type: "Structure" },
  { key: "lastRequestedUrl", label: "Last Requested URL", type: "Content" },
  { key: "milestones", label: "First Touch / Last Touch (milestones)", type: "Conversion" },
  { key: "touchpointType", label: "Touchpoint Type", type: "Conversion", exampleValues: ["call", "form", "sms"] },
  // Time of Day (1)
  { key: "hourOfDay", label: "Hour of Day / Time Zone", type: "Time" },
  // Form Identity (1)
  { key: "formName", label: "Form Name / URL", type: "Structure" },
];

// ============================================================================
// Helpers
// ============================================================================

interface CallRailMetadata {
  readonly allowedAccountIds: ReadonlyArray<string>;
  readonly conversationIntelligenceEntitled: boolean;
  readonly companyId?: string | null;
  /**
   * Story 22.5 AC #14 — whether the customer's CallRail account has call
   * recording enabled at the account level. Stamped by
   * `probeCallRailRecordingsEnabled` at connect time + on every
   * `validateConnection` health-check tick. The DataTable widget renders
   * the `▶ Play` button only when this is `true`; in fixture mode the
   * `loadCallRailFixture` helper force-stamps `true` regardless of the
   * KV value (Round 4 ADR-D entitlement-bypass pattern).
   *
   * Defensive default: `false`. If the side-channel is missing or the
   * probe hasn't run yet, the recording surface stays disabled.
   */
  readonly recordingsEnabled?: boolean;
}

/**
 * Public read accessor for the CallRail integration's recording-enabled
 * flag (Story 22.5 AC #14 / Task 5 issue route).
 *
 * Routes resolve this via the same KV side-channel that the adapter uses
 * for `conversationIntelligenceEntitled`. Returns `false` (the conservative
 * default) on missing KV / parse failure / transient KV error — the
 * route then short-circuits to `RECORDINGS_DISABLED` 403.
 */
export async function readCallRailRecordingsEnabled(
  env: { KV?: { get(key: string): Promise<string | null> } },
  integrationId: string,
): Promise<boolean> {
  const kv = env?.KV;
  if (!kv) return false;
  try {
    const raw = await kv.get(`callrail:meta:${integrationId}`);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { recordingsEnabled?: unknown };
    return parsed.recordingsEnabled === true;
  } catch {
    return false;
  }
}

/**
 * Read CallRail-specific metadata from the integration's KV side-channel.
 * The route handler is responsible for populating this at connect time;
 * the adapter reads it on every `fetchMetrics` to know whether to strip
 * CI-gated fields, whether the requested `companyId` is in scope, etc.
 *
 * Defensive: returns sensible defaults when the side-channel is missing
 * (first-fetch race condition, or test contexts without KV). The
 * `allowedAccountIds` defaults to `[ctx.accountId]` so the helper
 * cross-check trivially passes; the `conversationIntelligenceEntitled`
 * defaults to `false` so we strip CI fields conservatively.
 */
/**
 * Read the CallRail per-integration KV side-channel.
 *
 * Pass 1 review fix: previously the fallback for missing-KV / parse-
 * failure / transient-KV-error all returned `allowedAccountIds:
 * ctx.accountId ? [ctx.accountId] : []`, which silently coerced an
 * empty side-channel into "this single accountId is allowed". Combined
 * with the previous `length > 0` guard in fetchMetrics, that meant any
 * integration whose KV metadata was wiped (operator error, migration
 * bug, KV outage) would silently pass the cross-tenant guard.
 *
 * New behavior: return `allowedAccountIds: []` on any read failure —
 * the adapter's strict guard then rejects with `account_not_found`.
 * Operator remediation: re-authorize the integration through the
 * connect dialog (which re-populates the side-channel).
 */
async function readCallRailMetadata(
  ctx: AdapterContext,
): Promise<CallRailMetadata> {
  const empty: CallRailMetadata = {
    allowedAccountIds: [],
    conversationIntelligenceEntitled: false,
  };
  const kv = (ctx.env as { KV?: { get(key: string): Promise<string | null> } })?.KV;
  if (!kv) {
    return empty;
  }
  // Pass 3 review fix: removed the 500ms retry-with-backoff that
  // Pass 2 added. The retry was intended to bridge the KV eventual-
  // consistency window right after /connect, but in the steady-state
  // hot path it added 500ms of wasted wait to every fetchMetrics call
  // that hit a genuine KV miss (GDPR sweep, manual KV wipe, orphaned
  // integration). For the rare just-connected race window (~1-2s),
  // the queue's natural retry-on-`account_not_found` is the correct
  // mechanism — not an in-band sleep inside the Worker request handler.
  // This matches the Pass 1 fix that removed the 60-sec 429 cooldown
  // from `safeFetchCallRail` for the same reason.
  try {
    const key = `callrail:meta:${ctx.integrationId}`;
    const raw = await kv.get(key);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<CallRailMetadata>;
    return {
      allowedAccountIds: Array.isArray(parsed.allowedAccountIds)
        ? (parsed.allowedAccountIds as ReadonlyArray<string>)
        : [],
      conversationIntelligenceEntitled: Boolean(
        parsed.conversationIntelligenceEntitled,
      ),
      companyId:
        typeof parsed.companyId === "string" && parsed.companyId.length > 0
          ? parsed.companyId
          : null,
    };
  } catch {
    // Defensive fallback — return empty to trip the strict guard.
    return empty;
  }
}

function emitStructuredLog(
  event: string,
  payload: Record<string, unknown>,
): void {
  console.log(
    JSON.stringify({ event, ...payload, timestamp: new Date().toISOString() }),
  );
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * String-mode PII scrubber for error messages and structured-log fields
 * where the upstream provider may have echoed customer-identifying data.
 *
 * Targets the obvious shapes (E.164 phone numbers, email addresses) plus
 * known CallRail field-name prefixes in the form `customer_name="Jane Doe"`
 * or `customer_phone_number=+15551234`. Bounded character classes to
 * prevent catastrophic backtracking.
 *
 * Round 3 Red Team Attack #1 — Pass 1 review fix (callrail.ts:3692).
 */
function scrubPiiFromString(input: string): string {
  if (typeof input !== "string" || input.length === 0) return input;
  let out = input;
  // E.164-style phone numbers (`+15551234567`, `+1 (555) 123-4567` etc.)
  out = out.replace(
    /\+\d{1,3}[\s().-]?\(?\d{1,4}\)?[\s().-]?\d{1,4}[\s().-]?\d{1,9}/g,
    "[redacted-phone]",
  );
  // Pass 2 review fix: previous bare-10-digit pattern over-redacted
  // legitimate IDs / order numbers that happened to match the digit
  // shape (e.g. `INT-555-123-4567` → integration ID redacted as
  // phone). Tighten by requiring an explicit phone-context anchor
  // (`phone`, `caller`, `number`, `tel:`, or a leading `+`) — relies
  // on the field-name echo pattern below for the explicit-field case.
  out = out.replace(
    /(?<=\b(?:phone|caller|number|tel)[:= ]\s*)\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/gi,
    "[redacted-phone]",
  );
  // Email addresses
  out = out.replace(
    /\b[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9.-]{1,253}\.[A-Za-z]{2,24}\b/g,
    "[redacted-email]",
  );
  // CallRail field-name echoes: `customer_name="..."` / `customer_email=...`
  // Pass 2 review fix: also matches camelCase variants (CallRail webhook
  // payloads sometimes echo camelCase keys back) AND handles
  // unquoted values up to the next whitespace / control char.
  const fieldPattern =
    "(customer_(?:phone_number|name|email|city|state|country)|formatted_customer_location|transcription|conversational_transcript|call_summary|call_highlights|keywords_spotted|voicemail_transcription|customerPhoneNumber|customerName|customerEmail|customerCity|customerState|customerCountry|formattedCustomerLocation|conversationalTranscript|callSummary|callHighlights|keywordsSpotted|voicemailTranscription)";
  out = out.replace(
    new RegExp(`${fieldPattern}\\s*[:=]\\s*"[^"\\n]{1,4096}"`, "gi"),
    "$1=[redacted]",
  );
  out = out.replace(
    new RegExp(`${fieldPattern}\\s*[:=]\\s*'[^'\\n]{1,4096}'`, "gi"),
    "$1=[redacted]",
  );
  // Unquoted variant — terminate at whitespace, comma, or semicolon.
  out = out.replace(
    new RegExp(`${fieldPattern}\\s*[:=]\\s*[^\\s,;"'\\n]{1,4096}`, "gi"),
    "$1=[redacted]",
  );
  return out;
}

// ============================================================================
// Story 22.2 — Fixture-mode helper
// ============================================================================

/**
 * CallRail fixture-mode helper (Story 22.2 — closes Epic 22 CallRail slice).
 *
 * Story 22.1 deliberately deferred fixture-mode to Story 22.2; the adapter
 * shipped with NO fixture-mode short-circuit. This helper is added at the
 * TOP of `fetchMetrics()` and routes demo-workspace fetches through the
 * KV-cached fixture instead of the production CallRail v3 API.
 *
 * Mirrors `loadMozFixture` from Story 20.8 with three CallRail-specific
 * divergences:
 *
 * 1. **Retention-preflight-BYPASS in fixture mode** (Round 2 Pre-mortem C).
 *    The helper runs BEFORE `validateCallRailDateRangeAgainstRetention` so a
 *    fixture-generation-old date range never trips the 25-month ceiling.
 *    The fixture generator clamps dates to a 24-month floor at generation
 *    time; production-only retention enforcement is correct because the
 *    production API genuinely rejects > 25-month-old queries.
 *
 * 2. **Conversation Intelligence entitlement bypass** (Round 4 ADR-D). The
 *    helper does NOT read the `callrail:meta:{integrationId}` KV side-
 *    channel. Instead, it forces `conversationIntelligenceEntitled = true`
 *    UNCONDITIONALLY so the Sentiment Pie widget demos with populated
 *    state regardless of any KV-stored metadata.
 *
 * 3. **`dataFreshnessLagHours: 1`** stamping convention (Story 22.1 cache-
 *    TTL convention — CallRail's near-real-time data + 20-min post-call
 *    webhook delay max). The helper force-overwrites this field even when
 *    the fixture stamps a different value.
 *
 * **No production network calls** (Round 4 QA invariant — fixture-mode-vs-
 * production-mode separation): `safeFetchCallRail` /
 * `fetchCallRailCallsSummary` / `fetchCallRailCallsTimeseries` /
 * `fetchAllCallRailCalls` / `fetchAllCallRailFormSubmissions` /
 * `fetchAllCallRailTextMessages` / `discoverCallRailAccounts` /
 * `probeCallRailConversationIntelligence` are NEVER called.
 *
 * **No production cache writes** (Round 3 Red Team Attack #2 — production-
 * cache-write-prevention). The helper reads ONLY `fixture:callrail:{slug}`
 * keys and NEVER writes to ANY KV key. Production cache prefixes
 * (`callrail:*`, `callrail:meta:*`, `callrail:probe:*`) are not touched.
 *
 * **Cross-tenant fixture-data leak protection** (Round 3 Red Team Attack
 * #1). The helper resolves `clientSlug` via
 * `getClientSlug(db, ctx.tenantId, ctx.clientId)` — the SQL-layer
 * `(tenantId, clientId)` scope enforces isolation. Never bypass the
 * resolver.
 *
 * **Structured-log telemetry per AC #21** — emits one of
 * `callrail_demo_fixture_loaded` / `_missing` / `_binding_missing` per
 * invocation. NEVER emits PII (customer_phone_number / customer_name /
 * customer_email / transcription) — only the aggregate `recordCount`.
 */
async function loadCallRailFixture(
  ctx: AdapterContext,
  dateRange: { start: Date; end: Date },
): Promise<AdapterResult<AdapterMetrics>> {
  const env = ctx.env as unknown as Record<string, unknown>;
  const kv = env.KV as unknown;
  const db = env.DB as unknown;

  // AC #21 — binding-missing telemetry events.
  if (!kv || typeof (kv as { get?: unknown }).get !== "function") {
    emitStructuredLog("callrail_demo_fixture_binding_missing", {
      tenantId: ctx.tenantId,
      clientId: ctx.clientId,
      integrationId: ctx.integrationId,
      missingBinding: "kv",
    });
    return adapterErr({
      code: "permission_denied",
      message: "Missing KV binding — cannot load CallRail demo fixture",
      retryable: false,
    });
  }
  if (!db) {
    emitStructuredLog("callrail_demo_fixture_binding_missing", {
      tenantId: ctx.tenantId,
      clientId: ctx.clientId,
      integrationId: ctx.integrationId,
      missingBinding: "db",
    });
    return adapterErr({
      code: "permission_denied",
      message:
        "Missing DB binding — cannot resolve CallRail demo client slug",
      retryable: false,
    });
  }

  // Defensive validation that ctx.tenantId + ctx.clientId are non-empty
  // strings BEFORE calling getClientSlug. Round 3 Red Team Attack #1
  // cross-tenant leak protection.
  if (
    typeof ctx.tenantId !== "string" ||
    ctx.tenantId.length === 0 ||
    typeof ctx.clientId !== "string" ||
    ctx.clientId.length === 0
  ) {
    return adapterErr({
      code: "permission_denied",
      message:
        "CallRail demo fixture: ctx.tenantId and ctx.clientId must be non-empty strings",
      retryable: false,
    });
  }

  // Validate date range — reversed ranges silently return zero records and
  // Invalid Date crashes via toISOString. Return adapterErr for a typed
  // AdapterResult. Note: retention pre-flight is INTENTIONALLY skipped
  // (Round 2 Pre-mortem C — see top-of-function JSDoc).
  const startTime = dateRange.start?.getTime?.();
  const endTime = dateRange.end?.getTime?.();
  if (
    typeof startTime !== "number" ||
    !Number.isFinite(startTime) ||
    typeof endTime !== "number" ||
    !Number.isFinite(endTime)
  ) {
    return adapterErr({
      code: "invalid_request",
      message:
        "CallRail demo fixture: dateRange.start and dateRange.end must be finite Date instances",
      retryable: false,
    });
  }
  if (startTime > endTime) {
    return adapterErr({
      code: "invalid_request",
      message:
        "CallRail demo fixture: dateRange.start must be on or before dateRange.end",
      retryable: false,
    });
  }

  const { getFixtureFromKV, filterFixtureByDateRange } = await import(
    "../fixtures/kv-loader"
  );
  const { getClientSlug } = await import("../fixtures/demo-fetch");

  let clientSlug: string;
  try {
    // Round 3 Red Team Attack #1 — getClientSlug enforces the
    // `(tenantId, clientId)` scope at the SQL query layer. We MUST pass
    // both. Bypassing this resolver would silently leak fixture data
    // across tenants.
    clientSlug = await getClientSlug(
      db as Parameters<typeof getClientSlug>[0],
      ctx.tenantId,
      ctx.clientId,
    );
  } catch (err) {
    // Pass 1 Acceptance Auditor — emit a structured-log event so cross-
    // tenant defense failures are observable (Round 3 Red Team Attack #1
    // attempted-leak detection lives in the dashboard).
    emitStructuredLog("callrail_demo_fixture_resolver_failed", {
      tenantId: ctx.tenantId,
      clientId: ctx.clientId,
      integrationId: ctx.integrationId,
      reason: err instanceof Error ? err.message : String(err),
    });
    return adapterErr({
      code: "account_not_found",
      message: `CallRail demo client not found: ${err instanceof Error ? err.message : String(err)}`,
      retryable: false,
    });
  }

  const startedAt = Date.now();
  const metrics = await getFixtureFromKV(
    kv as Parameters<typeof getFixtureFromKV>[0],
    "callrail",
    clientSlug,
  );

  if (metrics === null) {
    emitStructuredLog("callrail_demo_fixture_missing", {
      tenantId: ctx.tenantId,
      clientId: ctx.clientId,
      integrationId: ctx.integrationId,
      clientSlug,
      kvKey: `fixture:callrail:${clientSlug}`,
    });
    return adapterErr({
      code: "account_not_found",
      message: `CallRail demo fixture not yet uploaded for client ${clientSlug}; run pnpm fixtures:upload`,
      retryable: false,
    });
  }

  const filtered = filterFixtureByDateRange(
    metrics,
    dateRange.start,
    dateRange.end,
  );

  // Warn when the date-range filter strips the fixture to zero records
  // (carry-forward from Moz 20.8 Pass 1 Blind Hunter — otherwise the UI
  // silently renders empty widgets with no diagnostic).
  const filteredWarnings = [...(filtered.warnings ?? [])];
  if (filtered.records.length === 0) {
    filteredWarnings.push({
      reportType: "*",
      reason: `CallRail demo fixture filtered to zero records for date range ${dateRange.start.toISOString().split("T")[0]} → ${dateRange.end.toISOString().split("T")[0]}. Try a wider window or check the fixture upload.`,
    });
  }

  // Force-overwrite `dataFreshnessLagHours: 1` per Story 22.1 cache-TTL
  // convention. Force-overwrite `conversationIntelligenceEntitled: true`
  // per Round 4 ADR-D (Sentiment Pie widget demos populated regardless).
  //
  // `dataFinalizedThrough` = `Date.now() - 1h` (CallRail near-real-time
  // freshness). If `dateRange.end` is narrower than `now`, clamp to
  // `min(now - 1h, dateRange.end - 1h)` so the demo doesn't claim "data
  // is from yesterday" when the user narrowed the date range.
  const lagMs = CALLRAIL_FRESHNESS_LAG_HOURS * 60 * 60 * 1000;
  const finalizedFromNow = Date.now() - lagMs;
  const finalizedFromRange = dateRange.end.getTime() - lagMs;
  const SANE_FINALIZED_FLOOR_MS = Date.UTC(2020, 0, 1);
  const dataFinalizedThroughMs =
    finalizedFromRange >= SANE_FINALIZED_FLOOR_MS
      ? Math.min(finalizedFromNow, finalizedFromRange)
      : finalizedFromNow;
  const dataFinalizedThrough = new Date(dataFinalizedThroughMs).toISOString();

  // Pass 1 Edge Case Hunter — surface a warning when the cached fixture's
  // metadata is missing/empty so a stale-schema fixture (older
  // fixtureSchemaVersion) gets flagged rather than silently emitting
  // defaults for currencyCode/timeZone/region.
  const cachedMeta = filtered.metadata ?? {};
  if (Object.keys(cachedMeta).length === 0) {
    filteredWarnings.push({
      reportType: "*",
      reason:
        "CallRail demo fixture has no metadata; re-run pnpm fixtures:generate + pnpm fixtures:upload to refresh.",
    });
  }
  // Pass 1 Edge Case Hunter — `ctx.accountId ?? "demo-800002"` allows
  // empty-string to leak through. Use a truthiness check that ALSO coerces
  // empty strings to the fallback (otherwise `allowedAccountIds: [""]`
  // would silently bypass downstream cross-tenant guards).
  const allowedAccountIdFallback =
    typeof ctx.accountId === "string" && ctx.accountId.length > 0
      ? ctx.accountId
      : "demo-800002";

  const result: AdapterMetrics = {
    ...filtered,
    warnings: filteredWarnings,
    metadata: {
      ...cachedMeta,
      dataFreshnessLagHours: CALLRAIL_FRESHNESS_LAG_HOURS,
      dataFinalizedThrough,
      conversationIntelligenceEntitled: true,
      // Story 22.5 AC #14 — fixture mode force-stamps recordings as enabled
      // so the demo workspace always renders the ▶ Play button. Mirrors the
      // CI entitlement force-stamp above (Round 4 ADR-D entitlement-bypass).
      recordingsEnabled: true,
      allowedAccountIds: [allowedAccountIdFallback],
      companyId: null,
    },
  };

  emitStructuredLog("callrail_demo_fixture_loaded", {
    tenantId: ctx.tenantId,
    clientId: ctx.clientId,
    integrationId: ctx.integrationId,
    clientSlug,
    recordCount: result.recordCount,
    durationMs: Date.now() - startedAt,
  });

  return adapterOk(result);
}

// ============================================================================
// Adapter
// ============================================================================

export const callRailAdapter: IntegrationAdapter = {
  platform: PLATFORM as Platform,
  authType: "api-key",
  rateLimits: {
    // CallRail documents 1K/hr general (= ~16.6/min raw); we declare 15/min
    // as the conservative ceiling, leaving headroom for the cron's
    // ~4 health-checks/hr. Burst limit 1 prevents thundering-herd across
    // queue consumers. Daily 10K matches the per-day cap per
    // rollout.com/integration-guides/call-rail/api-essentials 2026-05-14.
    requestsPerMinute: 15,
    burstLimit: 1,
    requestsPerDay: 10_000,
  },

  async validateConnection(
    ctx,
    credentials,
  ): Promise<AdapterResult<boolean>> {
    try {
      void ctx;
      const result = await validateCallRailConnection(credentials.accessToken);
      // Pass 1 review fix: a 0-account result means the token
      // authenticates but the user has zero accessible accounts (e.g.
      // their CallRail user permissions were revoked on every account
      // since connect). Treat this as `permission_denied` — the
      // integration is structurally broken, NOT healthy.
      if (result.accounts.length === 0) {
        return adapterErr({
          code: "permission_denied",
          message:
            "CallRail token is valid but has no accessible accounts. Check the user's CallRail permissions.",
          retryable: false,
        });
      }
      return adapterOk(result.ok);
    } catch (err) {
      return adapterErr(buildAdapterErrorFromUnknown(err));
    }
  },

  async fetchMetrics(
    ctx,
    credentials,
    dateRange,
  ): Promise<AdapterResult<AdapterMetrics>> {
    // Story 22.2 — fixture-mode short-circuit (AC #9). MUST run BEFORE any
    // production-path validation (retention pre-flight / metadata side-
    // channel / accountId check / Promise.allSettled fan-out) so a demo
    // workspace with no live CallRail API token still renders fixture data
    // without triggering token_invalid / account_not_found /
    // retention-violation errors. Round 2 Pre-mortem C: production-only
    // retention check (the fixture clamps to a 24-month floor at generation
    // time, so re-checking at fetch time creates a fixture-regeneration
    // trap every 25 months that would silently break demo mode).
    const fixturesEnabled =
      (ctx as unknown as { fixtures?: boolean }).fixtures === true;
    if (fixturesEnabled) {
      return loadCallRailFixture(ctx, dateRange);
    }

    // STEP 1 — 25-month retention pre-flight (AC #4, Round 2 Pre-mortem D).
    try {
      validateCallRailDateRangeAgainstRetention(dateRange);
    } catch (err) {
      if (err instanceof CallRailRetentionViolationError) {
        emitStructuredLog("callrail_retention_rejected", {
          integrationId: ctx.integrationId,
          tenantId: ctx.tenantId,
          requestedStart: err.requestedStart.toISOString(),
          retentionCutoff: err.retentionCutoff.toISOString(),
        });
        return adapterErr({
          code: "invalid_request",
          message: err.message,
          retryable: false,
        });
      }
      return adapterErr(buildAdapterErrorFromUnknown(err));
    }

    // STEP 2 — read KV side-channel for CI entitlement + allowed accounts.
    const metadata = await readCallRailMetadata(ctx);

    // Cross-tenant guard: the integration's stored accountId MUST be in
    // the discovered allowlist OR be the legacy single-account fallback.
    // Round 3 Red Team Attack #5 defense.
    if (!ctx.accountId) {
      return adapterErr({
        code: "account_not_found",
        message: "CallRail integration is missing account_id; re-authorize the integration via the connect flow.",
        retryable: false,
      });
    }
    // Pass 1 review fix (Round 3 Red Team Attack #5 defense hardening):
    // an EMPTY `allowedAccountIds` array previously short-circuited this
    // guard via the `length > 0` clause, allowing an arbitrary
    // `ctx.accountId` to pass. The empty array indicates a metadata
    // side-channel that was never populated OR was corrupted — neither
    // state should be treated as "scope-allowed".
    //
    // Permitted fallback: if the integration has a single accountId
    // configured AND the KV side-channel is empty (genuine first-fetch
    // race condition before metadata is written), accept the single
    // ctx.accountId as the authoritative scope. This matches the
    // `readCallRailMetadata` defaults.
    const isMetaEmpty = metadata.allowedAccountIds.length === 0;
    if (isMetaEmpty) {
      // No allowlist known — reject hard so a corrupted-metadata state
      // can't silently pass.
      return adapterErr({
        code: "account_not_found",
        message:
          "CallRail integration metadata is missing or empty. Re-authorize the integration via the connect flow.",
        retryable: false,
      });
    }
    if (!metadata.allowedAccountIds.includes(ctx.accountId)) {
      return adapterErr({
        code: "account_not_found",
        message: `CallRail account ${ctx.accountId} is not in the integration's allowedAccountIds — re-authorize the integration.`,
        retryable: false,
      });
    }

    const startDate = toIsoDate(dateRange.start);
    const endDate = toIsoDate(dateRange.end);

    // STEP 3 — fan-out the 4 endpoint groups in parallel.
    const sharedParams = {
      apiToken: credentials.accessToken,
      accountId: ctx.accountId,
      companyId: metadata.companyId ?? null,
      startDate,
      endDate,
    };

    // Pass 2 review fix: use the paginating wrappers for data-bearing
    // endpoints so customers with >100 records in the date range don't
    // get silently truncated. The summary + timeseries endpoints
    // return aggregate totals in a single response and don't need
    // pagination.
    const settled = await Promise.allSettled([
      fetchCallRailCallsSummary(sharedParams),
      fetchCallRailCallsTimeseries({ ...sharedParams, granularity: "day" }),
      fetchAllCallRailCalls({
        ...sharedParams,
        conversationIntelligenceEntitled:
          metadata.conversationIntelligenceEntitled,
        perPage: 250,
      }),
      fetchAllCallRailFormSubmissions({ ...sharedParams, perPage: 250 }),
      fetchAllCallRailTextMessages({ ...sharedParams, perPage: 250 }),
    ]);

    const records: Record<string, unknown>[] = [];
    const warnings: { reportType: string; reason: string }[] = [];
    const labels: ReadonlyArray<string> = [
      "calls-summary",
      "calls-timeseries",
      "calls-list",
      "form-submissions",
      "text-messages",
    ];

    for (let i = 0; i < settled.length; i++) {
      const result = settled[i]!;
      const label = labels[i]!;
      if (result.status === "fulfilled") {
        for (const row of result.value.records) {
          records.push(row);
        }
        // Surface any timeseries-cap warnings.
        if (
          label === "calls-timeseries" &&
          "warnings" in result.value &&
          Array.isArray(result.value.warnings)
        ) {
          for (const w of result.value.warnings) {
            warnings.push({ reportType: label, reason: w });
          }
        }
        // Pass 2 review fix: surface pagination-truncated warnings so
        // the widget renderer / customer is alerted when their date
        // range had more records than `CALLRAIL_DATA_MAX_PAGES × 250`
        // could fetch.
        if (
          "truncated" in result.value &&
          (result.value as { truncated?: boolean }).truncated === true
        ) {
          warnings.push({
            reportType: label,
            reason: `CallRail ${label} pagination capped at the maximum page count for this date range; some records may be omitted. Reduce the date range to ensure complete data.`,
          });
        }
        emitStructuredLog("callrail_api_call", {
          event: "callrail_api_call",
          endpoint: label,
          integrationId: ctx.integrationId,
          tenantId: ctx.tenantId,
          accountId: ctx.accountId,
          recordCount: result.value.records.length,
          pagesWalked:
            "pagesWalked" in result.value
              ? (result.value as { pagesWalked?: number }).pagesWalked
              : undefined,
          truncated:
            "truncated" in result.value
              ? (result.value as { truncated?: boolean }).truncated
              : undefined,
          status: "ok",
        });
      } else {
        // Failure surface: PII + token + provider-message scrubbed log +
        // warnings entry. Do NOT abort the whole sync; partial-surface-
        // unavailable is acceptable for this adapter (a 422 on CI-gated
        // calls fields shouldn't kill the summary/timeseries data).
        //
        // Round 3 Red Team Attack #1 defense (Pass 1 review fix): the
        // upstream provider-message can echo PII (e.g. customer phone
        // numbers in a malformed-request error body). Reuse the
        // PII-field allowlist by running the error message through a
        // lightweight string-mode scrubber.
        const rawReason =
          result.reason instanceof CallRailApiCallError
            ? result.reason.message
            : result.reason instanceof Error
              ? result.reason.message
              : String(result.reason);
        const reason = scrubPiiFromString(rawReason);
        warnings.push({ reportType: label, reason });
        emitStructuredLog("callrail_api_call_error", {
          endpoint: label,
          integrationId: ctx.integrationId,
          tenantId: ctx.tenantId,
          accountId: ctx.accountId,
          reason,
          status: "error",
        });
      }
    }

    // STEP 4 — final assembly. PII redaction is the responsibility of the
    // caller's log path; we keep PII in the persisted record shape so
    // widgets can render it (tenant-scoped access enforces security).
    //
    // Pass 1 review fix: if EVERY endpoint failed, surface as a structured
    // adapter error rather than silently reporting `recordCount: 0` —
    // otherwise the token-health monitor sees a "successful" fetch and
    // never trips `criticalOnConsecutiveFailures: 5`. Promote the
    // highest-priority error code observed across rejections.
    if (
      records.length === 0 &&
      settled.every((r) => r.status === "rejected")
    ) {
      // Promote the most authoritative error code: a 401 on any endpoint
      // means the token is invalid and the entire integration is broken;
      // a 403 means subscription/permission; otherwise treat as
      // provider_unavailable retryable.
      let chosenCode: import("../adapter").AdapterErrorCode =
        "provider_unavailable";
      let chosenMessage = "All CallRail endpoints failed for this sync";
      let retryable = true;
      for (const r of settled) {
        if (r.status !== "rejected") continue;
        const reason = r.reason;
        if (reason instanceof CallRailApiCallError) {
          if (reason.type === "unauthorized") {
            chosenCode = "token_expired";
            chosenMessage = reason.message;
            retryable = false;
            break;
          }
          if (reason.type === "forbidden") {
            chosenCode = "permission_denied";
            chosenMessage = reason.message;
            retryable = false;
          }
        }
      }
      return adapterErr({
        code: chosenCode,
        message: scrubPiiFromString(chosenMessage),
        retryable,
      });
    }

    const metrics: AdapterMetrics = {
      platform: PLATFORM as Platform,
      accountId: ctx.accountId,
      dateRange: { start: dateRange.start, end: dateRange.end },
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
      ...(warnings.length > 0 ? { warnings } : {}),
      metadata: {
        dataFreshnessLagHours: CALLRAIL_FRESHNESS_LAG_HOURS,
        conversationIntelligenceEntitled:
          metadata.conversationIntelligenceEntitled,
      },
    };
    return adapterOk(metrics);
  },

  async discoverAccounts(
    ctx,
    credentials,
  ): Promise<AdapterResult<AccountInfo[]>> {
    void ctx;
    try {
      const { accounts } = await discoverCallRailAccounts(
        credentials.accessToken,
      );
      const mapped: AccountInfo[] = accounts.map((a: CallRailAccount) => ({
        id: a.id,
        name: a.name,
        platform: PLATFORM as Platform,
        metadata: {
          outboundRecordingEnabled: a.outboundRecordingEnabled,
          hipaaAccount: a.hipaaAccount,
        },
      }));
      return adapterOk(mapped);
    } catch (err) {
      return adapterErr(buildAdapterErrorFromUnknown(err));
    }
  },

  async discoverEndpoints(
    ctx,
    _credentials,
  ): Promise<AdapterResult<EndpointDiscoveryInfo>> {
    // Read CI entitlement from the KV side-channel; the route handler
    // populated this at connect-time per AC #2.
    const metadata = await readCallRailMetadata(ctx);
    const ci = metadata.conversationIntelligenceEntitled;
    return adapterOk({
      endpoints: [
        { key: "calls-summary", status: "available" },
        { key: "calls-timeseries", status: "available" },
        { key: "calls-list", status: "available" },
        { key: "form-submissions", status: "available" },
        { key: "text-messages", status: "available" },
        ci
          ? { key: "conversation-intelligence", status: "available" as const }
          : {
              key: "conversation-intelligence",
              status: "subscription_required" as const,
              subscriptionRequired:
                "CallRail Premium Conversation Intelligence plan",
            },
      ],
    });
  },

  getAvailableMetrics(): MetricDefinition[] {
    return [...CALLRAIL_METRICS];
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return [...CALLRAIL_DIMENSIONS];
  },
};

// ============================================================================
// Error wrapper — internal
// ============================================================================

function buildAdapterErrorFromUnknown(err: unknown): {
  readonly code: import("../adapter").AdapterErrorCode;
  readonly message: string;
  readonly retryable: boolean;
  readonly providerStatus?: number;
} {
  if (err instanceof CallRailApiCallError) {
    return {
      code: callRailTypeToAdapterCode(err.type),
      message: err.message,
      retryable: err.retryable,
      providerStatus: err.status,
    };
  }
  const message = err instanceof Error ? err.message : String(err);
  return {
    code: "invalid_response",
    message,
    retryable: false,
  };
}

function callRailTypeToAdapterCode(
  type: import("./errors").CallRailErrorType,
): import("../adapter").AdapterErrorCode {
  switch (type) {
    case "unauthorized":
      return "token_expired";
    case "forbidden":
      return "permission_denied";
    case "not_found":
      return "account_not_found";
    case "validation_error":
      return "invalid_request";
    case "rate_limit_exceeded":
      return "rate_limited";
    case "provider_unavailable":
      return "provider_unavailable";
    case "unknown":
    default:
      return "invalid_response";
  }
}

/**
 * Public sanitize helper — exported so tests can verify the contract.
 * Just a re-export under a stable name; the implementation lives in
 * `pii.ts`.
 */
export { sanitizeCallRailPii };
